import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from 'api';

export type EarlyAccessJobRole =
  | 'founder'
  | 'hr_lead'
  | 'hiring_manager'
  | 'team_lead'
  | 'trusted_adviser'
  | 'influencer';

export interface EarlyAccessPayload {
  full_name: string;
  email: string;
  job_role: EarlyAccessJobRole;
  country_code: string;
  phone_number: string;
}

export interface EarlyAccessResponse {
  message: string;
  data: {
    id: number;
    full_name: string;
    email: string;
    job_role: string;
    country_code: string;
    phone_number: string;
  };
}

const extractErrorMessage = (errorData: any): string => {
  if (errorData?.error?.message && typeof errorData.error.message === 'object') {
    const fieldErrors = errorData.error.message;
    const errorMessages: string[] = [];

    Object.entries(fieldErrors).forEach(([field, errors]) => {
      if (Array.isArray(errors)) {
        const fieldLabel = field === 'phone_number' ? 'Phone number' :
                          field === 'full_name' ? 'Full name' :
                          field === 'email' ? 'Email' :
                          field === 'job_role' ? 'Job role' :
                          field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        errors.forEach((error: string) => {
          errorMessages.push(`${fieldLabel}: ${error}`);
        });
      }
    });

    return errorMessages.length > 0 ? errorMessages.join('. ') : 'Please check your information and try again.';
  }

  const extractedMessage = errorData?.error?.message ??
                          errorData?.message ??
                          errorData?.detail;

  return typeof extractedMessage === 'string'
    ? extractedMessage
    : 'Please check your information and try again.';
};

export const useEarlyAccess = () => {
  return useMutation({
    mutationFn: async (payload: EarlyAccessPayload) => {
      const response = await apiClient.post<EarlyAccessResponse>('/auth/early-access/', payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Early access request submitted successfully.');
    },
    onError: (err: any) => {
      let errorMessage = 'Failed to submit early access request. Please try again.';

      if (err?.response) {
        const errData = err.response.data;
        const status = err.response.status;

        if (status === 400) {
          errorMessage = extractErrorMessage(errData);
        } else if (status === 409) {
          errorMessage = 'You have already requested early access!';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = extractErrorMessage(errData);
        }
      } else if (err?.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
    },
  });
};
